import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import "../styles/components/MotionEffects.css";

// Elements that fade/rise in when scrolled into view
const REVEAL_SELECTORS = [
  ".about-center .about-content",
  ".about-center .image-side",
  ".about .about-text",
  ".values h2",
  ".values .container > p",
  ".values .step",
  ".cta",
  ".services .service-card",
  ".companies-page .card",
  ".pricing .pricing-card",
  ".service-detail .service-content",
  ".single-image",
  ".single-desc",
  ".contact form",
  ".map-wrapper",
  ".posts-grid .post-card",
  ".blog-post-content .post-content",
  ".site-footer .footer-grid > section",
].join(",");

// Images that drift at a different speed than the page (desktop only)
const PARALLAX_TARGETS = [
  [".hero .image-side", 0.08],
  [".about-center .image-side", 0.06],
  [".service-hero .service-image", 0.06],
  [".single-image", 0.05],
];

const STAGGER_MS = 90;
const MAX_PARALLAX = 60;

export default function MotionEffects() {
  const { pathname } = useLocation();

  useEffect(() => {
    const header = document.querySelector(".site-header");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Compact header once the page is scrolled
    const onHeaderScroll = () => {
      if (!header) return;
      if (window.scrollY > 8) header.setAttribute("data-scrolled", "");
      else header.removeAttribute("data-scrolled");
    };
    onHeaderScroll();
    window.addEventListener("scroll", onHeaderScroll, { passive: true });

    if (reduceMotion || !("IntersectionObserver" in window)) {
      return () => window.removeEventListener("scroll", onHeaderScroll);
    }

    const main = document.querySelector(".page-content");
    if (!main) {
      return () => window.removeEventListener("scroll", onHeaderScroll);
    }

    /* ---------- Scroll reveal ---------- */
    const revealObserver = new IntersectionObserver(
      (entries) => {
        let batch = 0;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.style.setProperty("--reveal-delay", `${Math.min(batch, 5) * STAGGER_MS}ms`);
          el.setAttribute("data-revealed", "");
          revealObserver.unobserve(el);
          batch += 1;
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );

    const registerReveals = () => {
      document.querySelectorAll(REVEAL_SELECTORS).forEach((el) => {
        if (el.hasAttribute("data-reveal")) return;
        // Content already on screen is never hidden (no flash, no hidden text for crawlers)
        if (el.getBoundingClientRect().top < window.innerHeight) return;
        el.setAttribute("data-reveal", "");
        revealObserver.observe(el);
      });
    };

    /* ---------- Parallax ---------- */
    const desktop = window.matchMedia("(min-width: 1024px)");
    let parallaxItems = [];
    let hero = null;
    let ticking = false;

    const registerParallax = () => {
      hero = document.querySelector(".hero");
      parallaxItems = [];
      PARALLAX_TARGETS.forEach(([selector, speed]) => {
        document.querySelectorAll(selector).forEach((el) => {
          el.setAttribute("data-parallax", "");
          parallaxItems.push({ el, speed, offset: 0 });
        });
      });
    };

    const updateParallax = () => {
      ticking = false;
      const vh = window.innerHeight;

      if (hero) {
        const heroBottom = hero.getBoundingClientRect().bottom;
        if (heroBottom > 0) hero.style.setProperty("--hero-scroll", String(window.scrollY));
      }

      if (!desktop.matches) {
        parallaxItems.forEach((item) => {
          if (item.offset) {
            item.offset = 0;
            item.el.style.setProperty("--parallax-y", "0px");
          }
        });
        return;
      }

      parallaxItems.forEach((item) => {
        const rect = item.el.getBoundingClientRect();
        const top = rect.top - item.offset; // position without our own shift
        if (top > vh || top + rect.height < 0) return;
        const distance = vh / 2 - (top + rect.height / 2);
        const offset = Math.max(-MAX_PARALLAX, Math.min(MAX_PARALLAX, distance * item.speed));
        item.offset = offset;
        item.el.style.setProperty("--parallax-y", `${offset.toFixed(1)}px`);
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateParallax);
    };

    /* ---------- Wire up (incl. async content like blog posts) ---------- */
    let scheduled = false;
    const refresh = () => {
      scheduled = false;
      registerReveals();
      registerParallax();
      updateParallax();
    };

    const mutationObserver = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(refresh);
    });

    // Wait a frame so ScrollToTop has reset the scroll position
    const startFrame = window.requestAnimationFrame(refresh);
    mutationObserver.observe(main, { childList: true, subtree: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.cancelAnimationFrame(startFrame);
      window.removeEventListener("scroll", onHeaderScroll);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      mutationObserver.disconnect();
      revealObserver.disconnect();
    };
  }, [pathname]);

  return null;
}
